// Ubicación en el repo: src/components/AppointmentFormModal.jsx
// Modal Crear/Editar cita (spec-pacientes-agenda.md 2.3, punto 6 del
// orden sugerido). Mismo componente para ambos casos: `appointment` viene
// con datos en edición, o null al crear (con date/time ya elegidos desde
// el slot en el que se hizo clic).
import { useEffect, useState } from 'react';
import {
  apptTypesForModule,
  isBlockType,
  DURATIONS,
  STATUS_ALL,
  createAppointment,
  updateAppointment,
  listPotentialConflicts,
  findOverlaps,
} from '../lib/appointments';
import { searchPatients, createQuickPatient } from '../lib/patients';
import { professionalsForModuleAndBranch } from '../lib/professionals';
import { formatPatientName, formatProfessionalName } from '../lib/format';
import { printHTML } from '../lib/print';
import { appointmentReceiptHTML } from '../lib/receipts';
import { getBranchLogoUrl } from '../lib/branchLogos';

function initialForm(appointment, defaults) {
  if (appointment) {
    return {
      patientId: appointment.patient_id,
      patientLabel: appointment.patients ? formatPatientName(appointment.patients) : '',
      type: appointment.type,
      date: appointment.date,
      time: appointment.time?.slice(0, 5) ?? '',
      duration: appointment.duration,
      professionalId: appointment.professional_id || '',
      notes: appointment.notes || '',
      status: appointment.status,
    };
  }
  return {
    patientId: null,
    patientLabel: '',
    type: '',
    date: defaults.date,
    time: defaults.time || '',
    duration: 30,
    professionalId: '',
    notes: '',
    status: 'Pendiente',
  };
}

export default function AppointmentFormModal({
  appointment,
  defaults,
  branchId,
  branch,
  module,
  professionals,
  actorId,
  onClose,
  onSaved,
}) {
  const isEdit = !!appointment;
  const [form, setForm] = useState(() => initialForm(appointment, defaults));
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickNombre, setQuickNombre] = useState('');
  const [quickApellido, setQuickApellido] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState(null); // null = sin chequear, [] = sin choques

  const blockType = isBlockType(form.type);

  useEffect(() => {
    if (!patientQuery.trim()) {
      setPatientResults([]);
      return;
    }
    const t = setTimeout(() => {
      searchPatients(patientQuery).then(setPatientResults).catch(() => setPatientResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [patientQuery]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setConflicts(null);
  }

  function selectPatient(p) {
    set('patientId', p.id);
    setForm((f) => ({ ...f, patientLabel: formatPatientName(p) }));
    setPatientQuery('');
    setPatientResults([]);
  }

  async function handleQuickCreate() {
    if (!quickNombre.trim() || !quickApellido.trim()) return;
    try {
      const patient = await createQuickPatient(quickNombre.trim(), quickApellido.trim(), branchId, actorId);
      selectPatient(patient);
      setShowQuickCreate(false);
      setQuickNombre('');
      setQuickApellido('');
    } catch (err) {
      setError(err.message || 'No se pudo crear el paciente.');
    }
  }

  function validate() {
    if (!form.type) return 'Elige un tipo de atención.';
    if (!form.date || !form.time) return 'Fecha y hora son obligatorias.';
    if (!blockType && !form.patientId) return 'Elige o crea un paciente (no es un bloqueo de agenda).';
    return '';
  }

  async function doSave() {
    const values = {
      patient_id: blockType ? null : form.patientId,
      type: form.type,
      date: form.date,
      time: form.time,
      duration: Number(form.duration),
      professional_id: form.professionalId || null,
      notes: form.notes.trim() || null,
      module,
      branch_id: branchId,
      status: form.status,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateAppointment(appointment.id, values, actorId);
      } else {
        await createAppointment(values, actorId);
      }
      // Se informa si cambió fecha/hora respecto de la cita original
      // (24-ago-2026, pedido de Nicolás) — Agenda.jsx usa esto para
      // ofrecer, sin bloquear, avisarle al paciente por correo del nuevo
      // horario. Mismo criterio para el arrastre en la grilla, ver
      // handleDrop() en Agenda.jsx.
      const dateTimeChanged =
        isEdit && (appointment.date !== values.date || (appointment.time || '').slice(0, 5) !== values.time);
      onSaved({ isEdit, dateTimeChanged, previous: appointment, values });
    } catch (err) {
      if (err.code === '23P01') {
        // Mensaje genérico a propósito (22-ago-2026, fix #2 de la
        // auditoría, DECISIONS.md): este código ahora lo puede disparar
        // tanto el EXCLUDE de "mismo profesional" (0001) como el trigger
        // nuevo de bloqueos sin profesional superpuestos (migración 0036).
        setError('Ya existe otra cita o bloqueo activo en ese horario — no se puede guardar (choque de horario).');
      } else {
        setError(err.message || 'No se pudo guardar la cita.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (conflicts === null) {
      // Primer intento: revisar choques antes de guardar (spec 2.3).
      try {
        const existing = await listPotentialConflicts({
          branchId,
          module,
          date: form.date,
          excludeId: appointment?.id,
        });
        const overlaps = findOverlaps(
          { time: form.time, duration: Number(form.duration), professionalId: form.professionalId || null },
          existing
        );
        if (overlaps.length > 0) {
          setConflicts(overlaps);
          return; // se le muestra el aviso, todavía no se guarda
        }
      } catch {
        // si el chequeo previo falla por red, se sigue igual — el
        // EXCLUDE de la base sigue protegiendo el caso "mismo profesional".
      }
    }

    await doSave();
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar cita' : 'Nueva cita'}</h2>
        {isEdit && appointment.booking_source === 'self_service' && (
          <p className="field-hint" style={{ marginTop: -8, marginBottom: 12 }}>
            🌐 Esta cita la agendó el propio paciente desde /agendamiento-online.
          </p>
        )}
        {isEdit && appointment.booking_source !== 'self_service' && (
          <p className="field-hint" style={{ marginTop: -8, marginBottom: 12 }}>
            🏢{' '}
            {appointment.creator
              ? `Agendada en sucursal por ${formatProfessionalName(appointment.creator, { withRole: true })}.`
              : 'Agendada en sucursal.'}
          </p>
        )}

        <form onSubmit={handleSubmit} className="patient-form">
          <label className="field">
            <span>Tipo de atención *</span>
            <select value={form.type} onChange={(e) => set('type', e.target.value)} required>
              <option value="">—</option>
              {apptTypesForModule(module).map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          {!blockType && (
            <div className="field">
              <span>Paciente *</span>
              {form.patientId ? (
                <div className="patient-picker-selected">
                  {form.patientLabel}
                  <button type="button" className="link-button" onClick={() => set('patientId', null)}>
                    Cambiar
                  </button>
                </div>
              ) : (
                <>
                  <input
                    value={patientQuery}
                    onChange={(e) => setPatientQuery(e.target.value)}
                    placeholder="Buscar por nombre o RUT…"
                  />
                  {patientResults.length > 0 && (
                    <ul className="patient-picker-results">
                      {patientResults.map((p) => (
                        <li key={p.id}>
                          <button type="button" onClick={() => selectPatient(p)}>
                            {formatPatientName(p)} {p.rut ? `— ${p.rut}` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!showQuickCreate ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setShowQuickCreate(true)}
                    >
                      + Crear paciente nuevo rápido
                    </button>
                  ) : (
                    <div className="quick-create-patient">
                      <input
                        placeholder="Nombre"
                        value={quickNombre}
                        onChange={(e) => setQuickNombre(e.target.value)}
                      />
                      <input
                        placeholder="Apellido"
                        value={quickApellido}
                        onChange={(e) => setQuickApellido(e.target.value)}
                      />
                      <button type="button" className="button-secondary" onClick={handleQuickCreate}>
                        Crear
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="form-row">
            <label className="field">
              <span>Fecha *</span>
              <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
            </label>
            <label className="field">
              <span>Hora *</span>
              <input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} required />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Duración</span>
              <select value={form.duration} onChange={(e) => set('duration', e.target.value)}>
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} min
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Profesional</span>
              <select value={form.professionalId} onChange={(e) => set('professionalId', e.target.value)}>
                <option value="">Sin asignar</option>
                {professionalsForModuleAndBranch(professionals, module, branchId).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre} {p.apellido} ({p.role})
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="field">
            <span>Notas</span>
            <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </label>

          {isEdit && (
            <label className="field">
              <span>Estado</span>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}>
                {STATUS_ALL.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <span className="field-hint">
                Un clic sobre la cita en la agenda avanza el estado solo; usa esto para marcar
                &quot;No se presentó&quot; u otro ajuste manual.
              </span>
            </label>
          )}

          {conflicts && conflicts.length > 0 && (
            <div className="conflict-warning">
              <strong>Choca con {conflicts.length} cita(s) en ese horario:</strong>
              <ul>
                {conflicts.map((c) => (
                  <li key={c.id}>
                    {c.time?.slice(0, 5)} · {c.type}
                    {c.professionals ? ` · ${c.professionals.nombre} ${c.professionals.apellido}` : ''}
                  </li>
                ))}
              </ul>
              <p>¿Guardar de todas formas?</p>
            </div>
          )}

          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            {isEdit && !blockType && (
              <button
                type="button"
                className="button-secondary"
                onClick={async () =>
                  printHTML(
                    `Comprobante de cita - ${form.patientLabel || 'Paciente'} - ${form.date}`,
                    appointmentReceiptHTML(appointment, branch, await getBranchLogoUrl(branch))
                  )
                }
              >
                🖨 Comprobante
              </button>
            )}
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            {conflicts && conflicts.length > 0 ? (
              <button type="button" className="button-danger" onClick={doSave} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar de todas formas'}
              </button>
            ) : (
              <button type="submit" className="button-primary" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
